import React from 'react';
import { X, CheckCircle, XCircle, Info } from 'lucide-react';

interface CrashDetails {
  crash_id: number;
  dot_number: number;
  report_state: string;
  report_number: string;
  report_date: string;
  report_time: string;
  report_seq_no: string;
  location: string;
  city: string;
  vehicle_identification_number: string;
  vehicle_license_number: string;
  vehicle_lic_state: string;
  fatalities: number;
  injuries: number;
  tow_away: string;
  federal_recordable: string;
  state_recordable: string;
  truck_bus_ind: string;
  hazmat_released: string;
  crash_carrier_name: string;
  vehicle_hazmat_placard: string;
  hazmat_class: string;
  hazmat_name: string;
  hazmat_id_number: string;
  first_harmful_event: string;
  most_harmful_event: string;
  is_preventable: boolean | null;
  created_at: string;
  crash_event_seq_id_desc: string;
  // Carrier lookup fallback
  carrier_name?: string;
}

interface CrashDetailsModalProps {
  crash: CrashDetails | null;
  onClose: () => void;
}

export const CrashDetailsModal: React.FC<CrashDetailsModalProps> = ({ crash, onClose }) => {
  if (!crash) return null;

  // Helper to get field value with both camelCase and snake_case support
  const getField = (snakeName: string, camelName: string): any => {
    return (crash as any)[snakeName] ?? (crash as any)[camelName];
  };

  const formatTime = (time: string): string => {
    if (!time || time.length !== 4) return time || 'Not recorded';
    const hours = time.substring(0, 2);
    const minutes = time.substring(2, 4);
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatDate = (dateInput: string | Date | null | undefined): string => {
    if (!dateInput) return 'Not recorded';
    
    let date: Date;
    
    // If already a Date object, use it directly
    if (dateInput instanceof Date) {
      date = dateInput;
    } else {
      const dateStr = String(dateInput);
      
      // Handle YYYYMMDD format (e.g., "20260131")
      if (/^\d{8}$/.test(dateStr)) {
        const year = dateStr.substring(0, 4);
        const month = dateStr.substring(4, 6);
        const day = dateStr.substring(6, 8);
        date = new Date(`${year}-${month}-${day}T00:00:00Z`);
      } else {
        // Try parsing as-is (ISO format or other)
        date = new Date(dateStr.includes('T') ? dateStr : dateStr + 'T00:00:00Z');
      }
    }
    
    if (isNaN(date.getTime())) return 'Not recorded';
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });
  };

  // Parse event sequence like "3:6:NONCOLLISION:EXPLOSION OR FIRE;1:1:NONCOLLISION:RAN OFF ROAD"
  const parseEventSequence = (eventSeq: string): { order: number; description: string; category: string }[] => {
    if (!eventSeq) return [];
    
    return eventSeq.split(';').map(event => {
      const parts = event.split(':');
      if (parts.length >= 4) {
        const category = parts[2];
        const description = parts.slice(3).join(':');
        return {
          order: parseInt(parts[0], 10),
          description: description.toLowerCase().replace(/\b\w/g, c => c.toUpperCase()),
          category: category === 'NONCOLLISION' ? 'Non-Collision' : 
                    category.toLowerCase().replace(/\b\w/g, c => c.toUpperCase())
        };
      }
      return { order: 0, description: event, category: '' };
    }).sort((a, b) => a.order - b.order);
  };

  // Get carrier name - prefer crash_carrier_name, fallback to carrier_name from carriers table
  const carrierName = getField('crash_carrier_name', 'crashCarrierName') || getField('carrier_name', 'carrierName') || 'Not recorded';

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: "var(--z-modal)",
        padding: 'var(--space-md)',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fbf9f4',
          borderRadius: 'var(--radius-lg)',
          maxWidth: '900px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: 'var(--space-lg)',
            borderBottom: '2px solid var(--color-gray-200)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'start',
            flexShrink: 0,
          }}
        >
          <div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-gray-900)', marginBottom: '0.5rem' }}>
              🚨 Crash Details
            </h2>
            <div style={{ fontSize: '0.875rem', color: 'var(--color-gray-600)', fontFamily: 'monospace' }}>
              Report: {getField('report_number', 'reportNumber') || 'N/A'} | DOT: {getField('dot_number', 'dotNumber') || 'N/A'}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 'var(--space-sm)',
              color: 'var(--color-gray-600)',
              borderRadius: 'var(--radius-md)',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--color-gray-100)';
              e.currentTarget.style.color = 'var(--color-gray-900)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = 'var(--color-gray-600)';
            }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Body - Scrollable */}
        <div
          style={{
            padding: 'var(--space-lg)',
            overflowY: 'auto',
            flex: 1,
          }}
        >
          {/* Preventability Status */}
          {(() => {
            const isPreventable = getField('is_preventable', 'isPreventable');

            if (isPreventable === null || isPreventable === undefined) {
              return (
                <div
                  style={{
                    padding: 'var(--space-md)',
                    background: '#efebe1',
                    border: '2px solid #23211d',
                    borderRadius: 'var(--radius-md)',
                    marginBottom: 'var(--space-lg)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-sm)',
                  }}
                >
                  <Info size={24} style={{ color: '#23211d', flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#34312c' }}>
                      Crash Preventability Status Not Available
                    </div>
                    <div style={{ fontSize: '0.8125rem', color: '#34312c', marginTop: '0.25rem' }}>
                      FMCSA CPDP data has not been imported for this crash.
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <div
                style={{
                  padding: 'var(--space-md)',
                  background: isPreventable === false ? '#d1e6d9' : '#f2dad2',
                  border: `2px solid ${isPreventable === false ? '#5d7148' : '#b3412c'}`,
                  borderRadius: 'var(--radius-md)',
                  marginBottom: 'var(--space-lg)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-sm)',
                }}
              >
                {isPreventable === false ? (
                  <CheckCircle size={24} style={{ color: '#4b5c3a', flexShrink: 0 }} />
                ) : (
                  <XCircle size={24} style={{ color: '#b3412c', flexShrink: 0 }} />
                )}
                <div>
                  <div style={{ fontSize: '0.875rem', fontWeight: 700, color: isPreventable === false ? '#4b5c3a' : '#7a2b1e' }}>
                    {isPreventable === false ? 'Non-Preventable Crash (CPDP Approved)' : 'Preventable Crash'}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Severity Overview */}
          {(() => {
            const fatalities = getField('fatalities', 'fatalities') || 0;
            const injuries = getField('injuries', 'injuries') || 0;
            const towAway = getField('tow_away', 'towAway');
            const hazmatReleased = getField('hazmat_released', 'hazmatReleased');

            return (
              <div style={{ marginBottom: 'var(--space-xl)' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 'var(--space-md)', color: 'var(--color-gray-900)' }}>
                  Severity Overview
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 'var(--space-md)' }}>
                  <div style={{ padding: 'var(--space-md)', background: fatalities > 0 ? '#f2dad2' : '#efebe1', borderRadius: 'var(--radius-md)' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: fatalities > 0 ? '#7a2b1e' : '#857f73', marginBottom: '0.25rem' }}>
                      Fatalities
                    </div>
                    <div style={{ fontSize: '1.75rem', fontWeight: 700, color: fatalities > 0 ? '#b3412c' : '#34312c' }}>
                      {fatalities}
                    </div>
                  </div>
                  <div style={{ padding: 'var(--space-md)', background: injuries > 0 ? '#efe3c8' : '#efebe1', borderRadius: 'var(--radius-md)' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: injuries > 0 ? '#664911' : '#857f73', marginBottom: '0.25rem' }}>
                      Injuries
                    </div>
                    <div style={{ fontSize: '1.75rem', fontWeight: 700, color: injuries > 0 ? '#963524' : '#34312c' }}>
                      {injuries}
                    </div>
                  </div>
                  <div style={{ padding: 'var(--space-md)', background: towAway === 'Y' ? '#f1e2c2' : '#efebe1', borderRadius: 'var(--radius-md)' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: towAway === 'Y' ? '#664911' : '#857f73', marginBottom: '0.25rem' }}>
                      Tow-Away
                    </div>
                    <div style={{ fontSize: '1.75rem', fontWeight: 700, color: towAway === 'Y' ? '#7d561c' : '#34312c' }}>
                      {towAway === 'Y' ? 'Yes' : 'No'}
                    </div>
                  </div>
                  <div style={{ padding: 'var(--space-md)', background: hazmatReleased === 'Y' ? '#f1d9e6' : '#efebe1', borderRadius: 'var(--radius-md)' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: hazmatReleased === 'Y' ? '#7a2b1e' : '#857f73', marginBottom: '0.25rem' }}>
                      Hazmat Released
                    </div>
                    <div style={{ fontSize: '1.75rem', fontWeight: 700, color: hazmatReleased === 'Y' ? '#b3412c' : '#34312c' }}>
                      {hazmatReleased === 'Y' ? 'Yes' : 'No'}
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Date, Time & Location */}
          <Section title="Date, Time & Location">
            <InfoRow label="Crash Date" value={formatDate(getField('report_date', 'reportDate'))} />
            <InfoRow label="Crash Time" value={formatTime(getField('report_time', 'reportTime'))} />
            <InfoRow label="Report State" value={getField('report_state', 'reportState') || 'Not specified'} />
            <InfoRow label="Location" value={getField('location', 'location') || 'Not specified'} />
            <InfoRow label="City" value={getField('city', 'city') || 'Not specified'} />
          </Section>

          {/* Vehicle Information - Simplified */}
          <Section title="Vehicle Information">
            <InfoRow label="VIN" value={getField('vehicle_identification_number', 'vehicleIdentificationNumber') || getField('vin', 'vin') || 'Not recorded'} monospace />
            <InfoRow label="License Plate" value={getField('vehicle_license_number', 'vehicleLicenseNumber') || 'Not recorded'} />
            <InfoRow label="Plate State" value={getField('vehicle_lic_state', 'vehicleLicState') || 'Not recorded'} />
            <InfoRow label="Vehicle Type" value={getField('truck_bus_ind', 'truckBusInd') === 'T' ? 'Truck' : getField('truck_bus_ind', 'truckBusInd') === 'B' ? 'Bus' : 'Unknown'} />
            <InfoRow label="Carrier" value={carrierName} />
            <InfoRow label="DOT Number" value={(getField('dot_number', 'dotNumber') || 'N/A').toString()} monospace />
          </Section>

          {/* Hazmat Details - only show if relevant */}
          {(getField('hazmat_released', 'hazmatReleased') === 'Y' || getField('vehicle_hazmat_placard', 'vehicleHazmatPlacard') || getField('hazmat_name', 'hazmatName')) && (
            <Section title="Hazardous Materials">
              <InfoRow label="Hazmat Placard" value={getField('vehicle_hazmat_placard', 'vehicleHazmatPlacard') || 'None'} />
              <InfoRow label="Hazmat Class" value={getField('hazmat_class', 'hazmatClass') || 'Not specified'} />
              <InfoRow label="Material Name" value={getField('hazmat_name', 'hazmatName') || 'Not specified'} />
              <InfoRow label="UN/NA Number" value={getField('hazmat_id_number', 'hazmatIdNumber') || 'Not specified'} />
            </Section>
          )}

          {/* Crash Events - only show if data exists */}
          {(getField('first_harmful_event', 'firstHarmfulEvent') || getField('most_harmful_event', 'mostHarmfulEvent') || getField('crash_event_seq_id_desc', 'crashEventSeqIdDesc')) && (
            <Section title="Crash Events">
              {(() => {
                const eventSeq = getField('crash_event_seq_id_desc', 'crashEventSeqIdDesc');
                const parsedEvents = parseEventSequence(eventSeq);
                
                if (parsedEvents.length > 0) {
                  return (
                    <div style={{ marginBottom: 'var(--space-md)' }}>
                      <div style={{ fontWeight: 600, color: 'var(--color-gray-700)', marginBottom: 'var(--space-sm)', fontSize: '0.875rem' }}>
                        Sequence of Events:
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}>
                        {parsedEvents.map((event, idx) => (
                          <div
                            key={idx}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 'var(--space-sm)',
                              padding: 'var(--space-sm) var(--space-md)',
                              background: event.category === 'Non-Collision' ? '#f1e2c2' : '#f2dad2',
                              borderRadius: 'var(--radius-sm)',
                              fontSize: '0.875rem',
                            }}
                          >
                            <span style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '24px',
                              height: '24px',
                              borderRadius: '50%',
                              background: event.category === 'Non-Collision' ? '#7d561c' : '#b3412c',
                              color: 'white',
                              fontWeight: 700,
                              fontSize: '0.75rem',
                              flexShrink: 0,
                            }}>
                              {event.order}
                            </span>
                            <span style={{ color: 'var(--color-gray-900)' }}>
                              {event.description}
                            </span>
                            <span style={{
                              marginLeft: 'auto',
                              fontSize: '0.75rem',
                              padding: '2px 8px',
                              background: event.category === 'Non-Collision' ? '#b07d2b' : '#cb6549',
                              color: event.category === 'Non-Collision' ? '#664911' : '#963524',
                              borderRadius: 'var(--radius-sm)',
                              fontWeight: 500,
                            }}>
                              {event.category}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }
                return null;
              })()}
              {getField('first_harmful_event', 'firstHarmfulEvent') && <InfoRow label="First Harmful Event" value={getField('first_harmful_event', 'firstHarmfulEvent')} />}
              {getField('most_harmful_event', 'mostHarmfulEvent') && <InfoRow label="Most Harmful Event" value={getField('most_harmful_event', 'mostHarmfulEvent')} />}
            </Section>
          )}

          {/* Recordability */}
          <Section title="Recordability">
            <InfoRow label="Federal Recordable" value={getField('federal_recordable', 'federalRecordable') === 'Y' ? 'Yes' : 'No'} />
            <InfoRow label="State Recordable" value={getField('state_recordable', 'stateRecordable') === 'Y' ? 'Yes' : 'No'} />
          </Section>
        </div>
      </div>
    </div>
  );
};

// Helper Components
interface SectionProps {
  title: string;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ title, children }) => (
  <div style={{ marginBottom: 'var(--space-xl)' }}>
    <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 'var(--space-md)', color: 'var(--color-gray-900)' }}>
      {title}
    </h3>
    <div style={{ background: '#f5f2ea', padding: 'var(--space-md)', borderRadius: 'var(--radius-md)', border: '1px solid #e2ddd1' }}>
      {children}
    </div>
  </div>
);

interface InfoRowProps {
  label: string;
  value: string;
  monospace?: boolean;
}

const InfoRow: React.FC<InfoRowProps> = ({ label, value, monospace = false }) => (
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: '200px 1fr',
      gap: 'var(--space-md)',
      padding: 'var(--space-sm) 0',
      borderBottom: '1px solid #e2ddd1',
      fontSize: '0.875rem',
    }}
  >
    <div style={{ fontWeight: 600, color: 'var(--color-gray-700)' }}>{label}:</div>
    <div style={{ color: 'var(--color-gray-900)', fontFamily: monospace ? 'monospace' : 'inherit' }}>{value}</div>
  </div>
);
